import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Collapse,
  useTheme,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Edit as EditIcon,
  Schedule as ScheduleIcon,
  Refresh as RefreshIcon,
  SearchOutlined,
} from '@mui/icons-material';
import Card from '../../common/Card';
import Button from '../../common/Button';
import CustomIconButton from '../../common/CustomIconButton';
import JobDetailsPopup from './JobDetailsPopup';
import { useAuthSlice } from '../../../redux/slice/authSlice';
import CustomMultipleDropDown from '../../common/CustomMultipleDropDown';
import { useForm } from 'react-hook-form';
import CustomTable from '../../common/CustomTable';
import { useAppDispatch } from '../../../redux/slice/redux-hooks';
import {
  useJobSlice,
  fetchJobsList,
  // scheduleJobAsync,
  // unscheduleJobAsync,
  runJobAsync,
  editJobAsync,
  changeJobStatusAsync,
  changeJobScheduleStatusAsync,
  // updateJobStatus,
  applyFilters,
  Job,
  SCHEDULE_TYPE_OPTIONS,
  ENABLED_OPTIONS,
  SCHEDULED_OPTIONS
} from '../../../redux/slice/jobSlice';
import { setSingleAlertObj } from '../../../redux/slice/commonSlice';
import { useModalStyles } from '../../common/useModalStyles';
import { useFilterPanelFocus } from "../../../hooks/useFilterPanelFocus";

interface JobFilters {
  jobName: string[];
  enabled: string[];
  scheduled: string[];
  scheduleType: string[];
}

const JobSchedulerList: React.FC = () => {
  const { userInfo } = useAuthSlice();
  const dispatch = useAppDispatch();
  //CS-1677
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { primaryBtnSx, tokens: modalTokens } = useModalStyles();
  const {
    jobs,
    allJobs,
  } = useJobSlice();

  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [isJobDetailsOpen, setIsJobDetailsOpen] = useState(false);
  const { control, handleSubmit, getValues } = useForm<JobFilters>({
    defaultValues: {
      jobName: [],
      enabled: ['YES'],
      scheduled: ['YES'],
      scheduleType: [],
    }
  });
  const [showCard, setShowCard] = useState(true);
  const {
    filterPanelRef,
    toggleFilterPanel,
    handleFilterPanelEntering,
    handleFilterPanelEntered,
    handleFilterPanelExited,
  } = useFilterPanelFocus(showCard, setShowCard);

  const listBorderColor = isDark ? "rgba(148,163,184,0.2)" : "rgba(15,23,42,0.12)";
  const listPanelBg = isDark ? "#141720" : "rgba(255,255,255,0.82)";
  const listHoverBg = isDark ? "rgba(20,184,166,0.09)" : "rgba(20,184,166,0.06)";

  const listCardSx = {
    borderRadius: "14px",
    border: `1px solid ${listBorderColor}`,
    backgroundColor: listPanelBg,
    backgroundImage: "none",
    backdropFilter: "blur(8px)",
    boxShadow: isDark ? "0 12px 32px rgba(0,0,0,0.28)" : "0 12px 28px rgba(15,23,42,0.08)",
  };

  const filterPanelSx = {
    ...listCardSx,
    p: { xs: "14px !important", md: "18px !important" },
    position: "relative",
    zIndex: 20,
    overflow: "visible",
    "& .MuiAutocomplete-root, & .MuiFormControl-root": { width: "100%" },
    "& .MuiTextField-root": { height: "42px", minHeight: "42px !important" },
    "& .MuiOutlinedInput-root, & .MuiInputBase-root, & .MuiAutocomplete-inputRoot, & .MuiAutocomplete-root .MuiOutlinedInput-root": {
      minHeight: "42px !important",
      height: "42px !important",
      borderRadius: "10px !important",
      backgroundColor: `${modalTokens.inputBg} !important`,
      color: modalTokens.textPrimary,
      fontSize: "0.88rem",
      boxShadow: isDark ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
      alignItems: "center",
      boxSizing: "border-box",
    },
    "& .MuiAutocomplete-root .MuiOutlinedInput-root": { padding: "0 38px 0 12px !important" },
    "& .MuiAutocomplete-root .MuiInputBase-input, & .MuiAutocomplete-input, & .MuiOutlinedInput-input": {
      paddingTop: "8px !important",
      paddingBottom: "8px !important",
      height: "24px !important",
      lineHeight: "24px !important",
      boxSizing: "border-box",
    },
    "& .MuiOutlinedInput-input": { paddingLeft: "12px !important", paddingRight: "12px !important" },
    "& .MuiAutocomplete-endAdornment": { top: "50% !important", transform: "translateY(-50%)", right: "8px !important" },
    "& .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root fieldset": { borderColor: `${modalTokens.inputBorder} !important` },
    "& .MuiOutlinedInput-root:hover fieldset, & .MuiOutlinedInput-root.Mui-focused fieldset": { borderColor: `${modalTokens.accent} !important`, borderWidth: "1px !important" },
    "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: isDark ? "none" : "0 0 0 3px rgba(20,184,166,0.12)" },
    "& .MuiInputBase-input": { color: modalTokens.textPrimary, fontSize: "0.88rem" },
    "& input::selection": { color: modalTokens.textPrimary, backgroundColor: isDark ? "rgba(20,184,166,0.28)" : "rgba(20,184,166,0.2)" },
    "& .MuiOutlinedInput-root:has(input:-webkit-autofill)": { backgroundColor: "#376c9f !important", "& fieldset": { borderColor: "#7fb1df !important" } },
    "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus": { WebkitTextFillColor: "#ffffff", caretColor: "#ffffff", WebkitBoxShadow: "0 0 0 100px #376c9f inset", borderRadius: "10px", transition: "background-color 5000s ease-in-out 0s" },
    "& .MuiInputLabel-root": { color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.45)", fontSize: "0.88rem" },
    "& .MuiInputLabel-root.Mui-focused": { color: `${modalTokens.accent} !important` },
    "& .MuiInputLabel-outlined:not(.MuiInputLabel-shrink)": { transform: "translate(14px, 9px) scale(1)" },
    "& .MuiChip-root": { borderRadius: "999px", backgroundColor: isDark ? "rgba(20,184,166,0.16)" : "rgba(20,184,166,0.1)", color: modalTokens.accent, border: `1px solid ${isDark ? "rgba(20,184,166,0.26)" : "rgba(20,184,166,0.18)"}`, fontWeight: 600 },
    "& .MuiSvgIcon-root": { color: modalTokens.textSecondary },
  };

  const primaryActionBtnSx = {
    ...primaryBtnSx,
    color: "#fff",
    "& .MuiTypography-root": { color: "#fff", fontWeight: 700 },
    "& .MuiButton-startIcon": { color: "#fff" },
  };

  const outlineBtnSx = {
    textTransform: "none" as const,
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "10px",
    py: 1.05,
    px: 1.6,
    color: "#00B8D9",
    borderColor: "rgba(0,184,217,0.55)",
    backgroundColor: isDark ? "rgba(0,184,217,0.08)" : "rgba(0,184,217,0.04)",
    whiteSpace: "nowrap" as const,
    "&:hover": { color: "#fff", borderColor: "#00B8D9", backgroundColor: "#00B8D9", boxShadow: "0 6px 22px rgba(0,184,217,0.28)" },
    "& .MuiTypography-root": { color: "inherit", fontWeight: 700 },
    "& .MuiButton-startIcon, & .MuiSvgIcon-root": { color: "inherit" },
  };

  const jobTableSx = {
    "& .MuiTableContainer-root": { borderRadius: "12px", border: "none", backgroundColor: "transparent" },
    "& table": {  borderCollapse: "collapse", borderSpacing: 0 },
    "& .MuiTableCell-root": { borderBottom: `1px solid ${isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)"}`, py: 1.15, fontSize: "0.84rem" },
    "& .MuiTableHead-root .MuiTableCell-root": { height: "42px"  },
    "& .MuiTableHead-root .MuiTypography-root": { color: "#00B8D9 !important", fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.02em" },
    "& .MuiTableBody-root .MuiTableRow-root": { transition: "background-color 0.18s ease, box-shadow 0.18s ease" },
    "& .MuiTableBody-root .MuiTableRow-root:hover": { backgroundColor: `${listHoverBg} !important` },
    "& .MuiTableBody-root .MuiTableCell-root": { color: modalTokens.textPrimary, fontSize: "0.84rem" },
    "& .MuiTableBody-root .MuiTypography-root": { fontSize: "0.84rem" },
    "& .MuiIconButton-root": { color: isDark ? "#94a3b8" : "#475569", borderRadius: "8px", "&:hover": { color: modalTokens.accent, backgroundColor: isDark ? "rgba(20,184,166,0.12)" : "rgba(20,184,166,0.08)" } },
    "& .MuiPaginationItem-root": { borderColor: listBorderColor, color: modalTokens.textSecondary, borderRadius: "8px", "&.Mui-selected": { color: "#fff", backgroundColor: modalTokens.accent, borderColor: modalTokens.accent } },
  };

  // Load jobs on component mount
  useEffect(() => {
    dispatch(fetchJobsList());
    reapplyCurrentFilters();
  }, [dispatch]);

  const handleSearch = (data: JobFilters) => {
    const filters: JobFilters = {
      jobName: data.jobName?.length ? data.jobName : [],
      enabled: data.enabled?.length ? data.enabled : [],
      scheduled: data.scheduled?.length ? data.scheduled : [],
      scheduleType: data.scheduleType?.length ? data.scheduleType : [],
    };
    dispatch(applyFilters(filters));
  };

  // Function to reapply current filters after job status changes
  const reapplyCurrentFilters = () => {
    const currentFormValues = getValues();
    handleSearch(currentFormValues);
  };

  const handleActivate = async (jobName: string) => {
    const job = jobs.find(j => j.jobName === jobName);
    if (!job) {
      dispatch(setSingleAlertObj({
        message: 'Job not found. Please refresh the page and try again.',
        alertType: 'error'
      }));
      return;
    }

    if (!job.jobId) {
      dispatch(setSingleAlertObj({
        message: 'Invalid job ID. Cannot activate job.',
        alertType: 'error'
      }));
      return;
    }

    try {
      const result = await dispatch(changeJobStatusAsync({
        jobId: job.jobId.toString(),
        auditUserId: userInfo?.userId?.toString(),
        isActive: true,
        isScheduled: job.scheduled
      })).unwrap();

      // Show success message from API response
      dispatch(setSingleAlertObj({
        message: result.response?.message || 'Job status changed successfully',
        alertType: 'success'
      }));

      reapplyCurrentFilters();
      handleRefresh();
    } catch (error: any) {
      console.error('Failed to activate job:', error);
    }
  };

  const handleInactivate = async (jobName: string) => {
    const job = jobs.find(j => j.jobName === jobName);
    if (!job) {
      dispatch(setSingleAlertObj({
        message: 'Job not found. Please refresh the page and try again.',
        alertType: 'error'
      }));
      return;
    }

    if (!job.jobId) {
      dispatch(setSingleAlertObj({
        message: 'Invalid job ID. Cannot inactivate job.',
        alertType: 'error'
      }));
      return;
    }

    try {
      const result = await dispatch(changeJobStatusAsync({
        jobId: job.jobId.toString(),
        auditUserId: userInfo?.userId?.toString(),
        isActive: false,
        isScheduled: job.scheduled
      })).unwrap();

      // Show success message from API response
      dispatch(setSingleAlertObj({
        message: result.response?.message || 'Job status changed successfully',
        alertType: 'success'
      }));

      reapplyCurrentFilters();
      handleRefresh();
    } catch (error: any) {
      console.error('Failed to inactivate job:', error);
    }
  };

  const handleScheduleService = async (jobName: string) => {
    const job = jobs.find(j => j.jobName === jobName);
    if (!job) {
      dispatch(setSingleAlertObj({
        message: 'Job not found. Please refresh the page and try again.',
        alertType: 'error'
      }));
      return;
    }

    if (!job.jobId) {
      dispatch(setSingleAlertObj({
        message: 'Invalid job ID. Cannot schedule job.',
        alertType: 'error'
      }));
      return;
    }

    try {
      const result = await dispatch(changeJobScheduleStatusAsync({
        jobId: job.jobId.toString(),
        auditUserId: userInfo?.userId?.toString(),
        isActive: (job.inActiveDate === null),
        isScheduled: true
      })).unwrap();

      // Show success message from API response
      dispatch(setSingleAlertObj({
        message: result.response?.message || 'Job schedule status changed successfully',
        alertType: 'success'
      }));

      reapplyCurrentFilters();
    } catch (error: any) {
      console.error('Failed to schedule job:', error);
    }
  };

  const handleUnscheduleService = async (jobName: string) => {
    const job = jobs.find(j => j.jobName === jobName);
    if (!job) {
      dispatch(setSingleAlertObj({
        message: 'Job not found. Please refresh the page and try again.',
        alertType: 'error'
      }));
      return;
    }

    if (!job.jobId) {
      dispatch(setSingleAlertObj({
        message: 'Invalid job ID. Cannot unschedule job.',
        alertType: 'error'
      }));
      return;
    }

    try {
      const result = await dispatch(changeJobScheduleStatusAsync({
        jobId: job.jobId.toString(),
        auditUserId: userInfo?.userId?.toString(),
        isActive: (job.inActiveDate === null),
        isScheduled: false
      })).unwrap();

      // Show success message from API response
      dispatch(setSingleAlertObj({
        message: result.response?.message || 'Job schedule status changed successfully',
        alertType: 'success'
      }));

      reapplyCurrentFilters();
    } catch (error: any) {
      console.error('Failed to unschedule job:', error);
    }
  };

  const handleEdit = (job: Job) => {
    setSelectedJob(job);
    setIsJobDetailsOpen(true);
  };

  const handleRunNow = async (jobName: string) => {
    if (!jobName || jobName.trim() === '') {
      dispatch(setSingleAlertObj({
        message: 'Invalid job name. Cannot run job.',
        alertType: 'error'
      }));
      return;
    }

    try {
      const result = await dispatch(runJobAsync(jobName)).unwrap();

      // Show success message from API response
      dispatch(setSingleAlertObj({
        message: result.response?.message || 'Job executed successfully',
        alertType: 'success'
      }));
      reapplyCurrentFilters();
      dispatch(fetchJobsList());
    } catch (error: any) {
      console.error('Failed to run job:', error);
    }
  };

  const handleRefresh = () => {
    dispatch(fetchJobsList());
  };

  const handleCloseJobDetails = () => {
    setIsJobDetailsOpen(false);
    setSelectedJob(null);
  };

  // Handle job edit save
  const handleSaveJob = async (editJobRequest: any) => {
    try {
      const result = await dispatch(editJobAsync(editJobRequest)).unwrap();

      // Show success message from API response
      dispatch(setSingleAlertObj({
        message: result.response?.message || 'Job details updated successfully',
        alertType: 'success'
      }));

      dispatch(fetchJobsList());
    } catch (error: any) {
      console.error('Failed to save job:', error);
    }
  };

const formatTo12Hour = (time: string | null | undefined): string => {
  if (!time) return 'N/A';
  const [hourStr, minute] = time.split(':');
  const hour = parseInt(hourStr, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minute} ${ampm}`;
};

  // Table columns configuration
  const columns = [
    { id: 'jobName', label: 'Agent Name' },
    { id: 'scheduleType', label: 'Schedule Type' },
    { id: 'dayOfWeek', label: 'Schedule Day' },
    { id: 'scheduleTime', label: 'Schedule Time' },
    { id: 'description', label: 'Description' },//CS-1571
    { id: 'className', label: 'Class Name' },
    { id: 'enabled', label: 'Enabled' },
    { id: 'scheduled', label: 'Scheduled' },
    { id: 'lastRunTime', label: 'Last Run Time' },
    { id: 'lastSyncDateTime', label: 'Last Sync Time' },
    { id: 'action', label: 'Actions' },
  ];
  // Transform jobs data for the table
  const tableRows = jobs.map(job => ({
    ...job,
    enabled: job.enabled ? 'YES' : 'NO',
    scheduled: job.scheduled ? 'YES' : 'NO',
    scheduleTime: formatTo12Hour(job.scheduleTime),
    scheduleType: job.scheduleType ? job.scheduleType.toUpperCase() : 'N/A',
    //CS-1571 - Keep full description for tooltip, truncation will be handled in CustomTable
    description: job.description || ' - ',
  }));

  // Action component for each row
  const actionComponent = (row: any) => {
    const isEnabled = row.enabled === 'YES';
    const isScheduled = row.scheduled === 'YES';

    return (
      <Box>
        {/* Stop/Inactivate Button - Only show if job is currently enabled */}
        {isEnabled && (
          <CustomIconButton
            tooltipMessage="Stop/Inactivate"
            tooltipPlacement="top"
            color="error"
            size="small"
            variant="outlined"
            onClick={() => handleInactivate(row.jobName)}
          >
            <StopIcon fontSize="small" />
          </CustomIconButton>
        )}

        {/* Start/Activate Button - Only show if job is currently disabled */}
        {!isEnabled && (
          <CustomIconButton
            tooltipMessage="Start/Activate"
            tooltipPlacement="top"
            color="success"
            size="small"
            variant="outlined"
            onClick={() => handleActivate(row.jobName)}
          >
            <PlayIcon fontSize="small" />
          </CustomIconButton>
        )}

        {/* Edit Button - Always show */}
        <CustomIconButton
          tooltipMessage="Edit Agent Details"
          tooltipPlacement="top"
          color="info"
          size="small"
          variant="outlined"
          onClick={() => {
            const originalJob = jobs.find(job => job.jobId === row.jobId);
            if (originalJob) handleEdit(originalJob);
          }}
        >
          <EditIcon fontSize="small" />
        </CustomIconButton>

        {/* Schedule Button - Only show if job is not scheduled and is enabled */}
        {!isScheduled && isEnabled && (
          <CustomIconButton
            tooltipMessage="Schedule Service"
            tooltipPlacement="top"
            color="warning"
            size="small"
            variant="outlined"
            onClick={() => handleScheduleService(row.jobName)}
          >
            <ScheduleIcon fontSize="small" />
          </CustomIconButton>
        )}

        {/* Unschedule Button - Only show if job is currently scheduled */}
        {isScheduled && (
          <CustomIconButton
            tooltipMessage="Unschedule Service"
            tooltipPlacement="top"
            color="warning"
            size="small"
            variant="outlined"
            onClick={() => handleUnscheduleService(row.jobName)}
          >
            <ScheduleIcon fontSize="small" />
          </CustomIconButton>
        )}

        {/* Run Now Button - Only show if job is enabled */}
        {isEnabled && (
          <CustomIconButton
            tooltipMessage="Run Job Now"
            tooltipPlacement="top"
            color={theme.palette.mode === "dark" ? "secondary" : "primary"} //CS-1677
            size="small"
            variant="outlined"
            onClick={() => handleRunNow(row.jobName)}
          >
            <RefreshIcon fontSize="small" />
          </CustomIconButton>
        )}
      </Box>
    );
  };

  // Dropdown options
  const jobNameOptions = [
    ...Array.from(new Set(allJobs.map(job => job.jobName))).map(name => ({
      value: name,
      label: name
    }))
  ];

  const typeOptions = [
    ...Array.from(new Set(allJobs.map(job => job.type))).map(type => ({
      value: type,
      label: type
    }))
  ];

  const scheduleTypeOptions = SCHEDULE_TYPE_OPTIONS.map(option => ({
    value: option.value,
    label: option.label
  }));

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        px: { xs: 1, md: 3 },
        py: 2,
      }}
    >
      <Box sx={{ position: "relative", zIndex: 20, overflow: "visible" }}>
        <Collapse in={showCard} ref={filterPanelRef} onEntering={handleFilterPanelEntering} onEntered={handleFilterPanelEntered} onExited={handleFilterPanelExited}>
          <Card sx={filterPanelSx}>
            <form onSubmit={handleSubmit(handleSearch)}>
              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={12} sm={6} md={2}>
                  <CustomMultipleDropDown
                    control={control}
                    name="jobName"
                    label="Agent Name"
                    options={jobNameOptions}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <CustomMultipleDropDown
                    control={control}
                    name="scheduleType"
                    label="Schedule Type"
                    options={scheduleTypeOptions}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <CustomMultipleDropDown
                    control={control}
                    name="enabled"
                    label="Enabled"
                    options={ENABLED_OPTIONS}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <CustomMultipleDropDown
                    control={control}
                    name="scheduled"
                    label="Scheduled"
                    options={SCHEDULED_OPTIONS}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Button
                    fullWidth
                    size="large"
                    startIcon={<SearchOutlined />}
                    type="submit"
                    removeUpperCase
                    sx={{ ...primaryActionBtnSx, height: "42px", py: 0 }}
                  >
                    Search
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Card>
        </Collapse>
      </Box>

      <Box mt={2} sx={{ position: "relative", zIndex: 1 }}>
        <Card sx={{ ...listCardSx, p: { xs: "14px !important", md: "18px !important" } }}>
          <Grid container justifyContent="space-between" alignItems="center" spacing={1.2}>
            <Grid item xs={12} md>
              <Typography variant="h3" fontWeight={800} color="text.primary" data-testid="header">
                Agent
              </Typography>
            </Grid>
            <Grid item xs={12} sm="auto">
              <Button
                variant="outlined"
                color="info"
                onClick={toggleFilterPanel}
                startIcon={<SearchOutlined />}
                fullWidth
                removeUpperCase
                sx={outlineBtnSx}
              >
                {showCard ? "Hide Filters" : "Show Filters"}
              </Button>
            </Grid>
            <Grid item xs={12} sm="auto">
              <Button
                variant="outlined"
                color="info"
                onClick={handleRefresh}
                startIcon={<RefreshIcon />}
                fullWidth
                removeUpperCase
                sx={outlineBtnSx}
              >
                Refresh
              </Button>
            </Grid>
            <Grid item xs={12}>
              <CustomTable
                columns={columns}
                rows={tableRows}
                actionComponent={actionComponent}
                borderBox={false}
                showMenu={true}
                sx={jobTableSx}
              />
            </Grid>
          </Grid>
        </Card>
      </Box>

      {/* Job Details Popup */}
      {selectedJob && (
        <JobDetailsPopup
          open={isJobDetailsOpen}
          job={selectedJob}
          onClose={handleCloseJobDetails}
          onSave={handleSaveJob}
        />
      )}
    </Box>
  );
};

export default JobSchedulerList;
